import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export const api = axios.create({ baseURL: BASE, withCredentials: true });

let refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthCall = url.includes("/auth/") || url.includes("/me");
    if (status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  },
);

export function apiError(detail, fallback = "Coś poszło nie tak. Spróbuj ponownie.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const authApi = {
  me: () => api.get("/me").then((r) => r.data),
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  googleSession: (session_id) => api.post("/auth/google/session", { session_id }).then((r) => r.data),
  register: (payload) => api.post("/auth/register", payload).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  logoutAll: () => api.post("/auth/logout-all").then((r) => r.data),
  updateMe: (payload) => api.patch("/me", payload).then((r) => r.data),
  forgot: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
  reset: (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data),
  changePassword: (current_password, new_password) => api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }).then((r) => r.data),
  resendVerification: (email) => api.post("/auth/resend-verification", { email }).then((r) => r.data),
};

export const libApi = {
  publicPackages: () => api.get("/packages").then((r) => r.data),
  library: () => api.get("/library").then((r) => r.data),
  package: (slug) => api.get(`/library/packages/${slug}`).then((r) => r.data),
  prompt: (slug) => api.get(`/library/prompts/${slug}`).then((r) => r.data),
  favorites: () => api.get("/favorites").then((r) => r.data),
  addFavorite: (slug) => api.post(`/favorites/${slug}`).then((r) => r.data),
  removeFavorite: (slug) => api.delete(`/favorites/${slug}`).then((r) => r.data),
  saveHistory: (slug, payload) => api.post(`/prompts/${slug}/history`, payload).then((r) => r.data),
  promptHistory: (slug) => api.get(`/prompts/${slug}/history`).then((r) => r.data),
  recentHistory: () => api.get("/history").then((r) => r.data),
};

export const matchApi = {
  match: (payload) => api.post("/match", payload).then((r) => r.data),
};

export const eventsApi = {
  // Anonimowe zdarzenie: kliknięcie „Skopiuj i otwórz" + narzędzie + typ promptu.
  // Fire-and-forget — nigdy nie blokuje UX i nie przesyła treści promptu.
  toolOpen: (tool_id, prompt_type, tool_name) =>
    api.post("/events/tool-open", { event: "copy_open", tool_id, prompt_type, tool_name }).catch(() => {}),
};

export const assistantApi = {
  // Dopasowanie potrzeby (język naturalny) do max 3 kreatorów. Bez kredytów.
  suggest: (need) => api.post("/assistant/suggest", { need }).then((r) => r.data),
  // Zagregowany licznik otwarć kreatora z rekomendacji. Bez PII.
  trackOpen: (method, category) =>
    api.post("/assistant/track-open", { method, category }).then((r) => r.data).catch(() => null),
  // Pomoc dla konkretnego pola formularza. Bez kredytów, bez wpisywania danych.
  fieldHelp: (slug, field_id, current_value) =>
    api.post("/assistant/field-help", { slug, field_id, current_value }).then((r) => r.data),
};

// Publiczne (bez logowania, bez interceptora auth) — publiczny Asystent + podgląd kreatora.
const publicAxios = axios.create({ baseURL: BASE });
export const publicApi = {
  assistantSuggest: (need) => publicAxios.post("/public/assistant/suggest", { need }).then((r) => r.data),
  promptPreview: (slug) => publicAxios.get(`/public/prompts/${slug}/preview`).then((r) => r.data),
  welcomeBonus: () => publicAxios.get("/public/welcome-bonus").then((r) => r.data),
};

export const feedbackApi = {
  submit: (formData) => api.post("/feedback", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  adminList: (params) => api.get("/admin/feedback", { params }).then((r) => r.data),
  adminSetStatus: (id, status) => api.patch(`/admin/feedback/${id}/status`, { status }).then((r) => r.data),
  screenshotUrl: (id) => `${BASE}/admin/feedback/${id}/screenshot`,
};

// Dział CV (zalogowani): wywiad + autozapis draftu (wspólny model danych).
export const cvApi = {
  create: (payload) => api.post("/cv", payload).then((r) => r.data),
  list: () => api.get("/cv").then((r) => r.data),
  latestDraft: (tryb = "nowe") => api.get(`/cv/latest-draft?tryb=${tryb}`).then((r) => r.data),
  get: (id) => api.get(`/cv/${id}`).then((r) => r.data),
  patch: (id, data) => api.patch(`/cv/${id}`, { data }).then((r) => r.data),
  assist: (payload) => api.post("/cv/assist", payload).then((r) => r.data),
  assistEvent: (payload) => api.post("/cv/assist/event", payload).then((r) => r.data).catch(() => {}),
  templates: () => api.get("/cv/templates").then((r) => r.data),
  renderHtml: (id, params) => api.get(`/cv/${id}/render`, { params, responseType: "text" }).then((r) => r.data),
  exportBlob: (id, fmt, params) => api.get(`/cv/${id}/export/${fmt}`, { params, responseType: "blob" }).then((r) => r.data),
  // Listy motywacyjne (CV-B10)
  letterList: () => api.get("/cv/letters").then((r) => r.data),
  letterCreate: (payload) => api.post("/cv/letters", payload).then((r) => r.data),
  letterGet: (id) => api.get(`/cv/letters/${id}`).then((r) => r.data),
  letterPatch: (id, data) => api.patch(`/cv/letters/${id}`, { data }).then((r) => r.data),
  letterGenerate: (id, variant = false) => api.post(`/cv/letters/${id}/generate${variant ? "?variant=true" : ""}`).then((r) => r.data),
  letterRestore: (id, index) => api.post(`/cv/letters/${id}/restore`, { index }).then((r) => r.data),
  letterExportBlob: (id, fmt) => api.get(`/cv/letters/${id}/export/${fmt}`, { responseType: "blob" }).then((r) => r.data),
  letterExtractOffer: (id) => api.post(`/cv/letters/${id}/extract-offer`).then((r) => r.data),
  matchOffer: (payload) => api.post("/cv/match", payload).then((r) => r.data),
  matchHistory: (cvId) => api.get(`/cv/match/history${cvId ? `?cv_id=${cvId}` : ""}`).then((r) => r.data),
  matchHistoryItem: (id) => api.get(`/cv/match/history/${id}`).then((r) => r.data),
  uploadPhoto: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/cv/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deletePhoto: (id) => api.delete(`/cv/${id}/photo`).then((r) => r.data),
  // Moje aplikacje (CV-B12)
  appList: () => api.get("/cv/applications").then((r) => r.data),
  appCreate: (payload) => api.post("/cv/applications", payload).then((r) => r.data),
  appPatch: (id, data) => api.patch(`/cv/applications/${id}`, { data }).then((r) => r.data),
  appDelete: (id) => api.delete(`/cv/applications/${id}`).then((r) => r.data),
  employersSearch: (params) => api.get("/cv/employers/search", { params }).then((r) => r.data),
};

export const contactApi = {
  send: (payload) => api.post("/contact", payload).then((r) => r.data),
};

// PROMPTIVO BIZNES — Generator (Etap 1). Obsługa gościa przez nagłówek x-guest-token.
const GUEST_KEY = "promptivo.biz.guestToken";
export function getBizGuestToken() {
  try {
    let t = localStorage.getItem(GUEST_KEY);
    if (!t) { t = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2)); localStorage.setItem(GUEST_KEY, t); }
    return t;
  } catch { return ""; }
}
const bizHeaders = () => ({ headers: { "x-guest-token": getBizGuestToken() } });
const BIZ = `${process.env.REACT_APP_BACKEND_URL}/api/business`;
export const businessApi = {
  config: () => api.get(`${BIZ}/ideas/config`, bizHeaders()).then((r) => r.data),
  createSession: (answers = {}) => api.post(`${BIZ}/ideas/sessions`, { answers, guestToken: getBizGuestToken() }, bizHeaders()).then((r) => r.data),
  getSession: (id) => api.get(`${BIZ}/ideas/sessions/${id}`, bizHeaders()).then((r) => r.data),
  patchSession: (id, data) => api.patch(`${BIZ}/ideas/sessions/${id}`, data, bizHeaders()).then((r) => r.data),
  generate: (id, rerun = false) => api.post(`${BIZ}/ideas/sessions/${id}/generate${rerun ? "?rerun=true" : ""}`, {}, bizHeaders()).then((r) => r.data),
  select: (id, candidateId) => api.post(`${BIZ}/ideas/sessions/${id}/select`, { candidateId }, bizHeaders()).then((r) => r.data),
  claim: () => api.post(`${BIZ}/guest/claim`, { guestToken: getBizGuestToken() }).then((r) => r.data),
  myProjects: () => api.get(`${BIZ}/me`).then((r) => r.data),
  exportUrl: (id, fmt) => `${BIZ}/ideas/sessions/${id}/export?format=${fmt}`,
};

export const analysisApi = {
  config: (entry) => api.get(`${BIZ}/analysis/config`, { ...bizHeaders(), params: { entry } }).then((r) => r.data),
  createSession: (entry, answers = {}) => api.post(`${BIZ}/analysis/sessions`, { entry, answers, guestToken: getBizGuestToken() }, bizHeaders()).then((r) => r.data),
  getSession: (id) => api.get(`${BIZ}/analysis/sessions/${id}`, bizHeaders()).then((r) => r.data),
  patchSession: (id, data) => api.patch(`${BIZ}/analysis/sessions/${id}`, data, bizHeaders()).then((r) => r.data),
  run: (id, rerun = false) => api.post(`${BIZ}/analysis/sessions/${id}/run${rerun ? "?rerun=true" : ""}`, {}, bizHeaders()).then((r) => r.data),
  myAnalyses: () => api.get(`${BIZ}/analysis/me`).then((r) => r.data),
  exportUrl: (id, fmt) => `${BIZ}/analysis/sessions/${id}/export?format=${fmt}`,
  emailReport: (id, payload) => api.post(`${BIZ}/analysis/sessions/${id}/email`, payload, bizHeaders()).then((r) => r.data),
  emailHistory: (id) => api.get(`${BIZ}/analysis/sessions/${id}/emails`, bizHeaders()).then((r) => r.data),
};

export const creditsApi = {
  balance: () => api.get("/credits/balance").then((r) => r.data),
  packs: () => api.get("/credits/packs").then((r) => r.data),
  actionPrices: () => api.get("/credits/action-prices").then((r) => r.data),
  quote: (actionKey) => api.post("/credits/quote", { actionKey }).then((r) => r.data),
  history: (cursor) => api.get("/credits/history", { params: cursor ? { cursor } : {} }).then((r) => r.data),
  checkout: (payload) => api.post("/credits/checkout", payload).then((r) => r.data),
  purchase: (purchaseId) => api.get(`/credits/purchases/${purchaseId}`).then((r) => r.data),
};

export const aiApi = {
  quote: (actionKey) => api.post("/ai/quote", { actionKey }).then((r) => r.data),
  run: (payload) => api.post("/ai/run", payload).then((r) => r.data),
  generatePrompt: (payload) => api.post("/ai/generate-prompt", payload).then((r) => r.data),
};

export const adminApi = {
  overview: (days = 30) => api.get("/admin/overview", { params: { days } }).then((r) => r.data),
  messages: (params) => api.get("/admin/messages", { params }).then((r) => r.data),
  updateMessage: (id, payload) => api.patch(`/admin/messages/${id}`, payload).then((r) => r.data),
  users: (params) => api.get("/admin/users", { params }).then((r) => r.data),
  user: (id) => api.get(`/admin/users/${id}`).then((r) => r.data),
  updateUser: (id, payload) => api.patch(`/admin/users/${id}`, payload).then((r) => r.data),
  userActionLog: (id) => api.get(`/admin/users/${id}/action-log`).then((r) => r.data),
  accountActions: (params) => api.get("/admin/account-actions", { params }).then((r) => r.data),
  adjustCredits: (id, payload) => api.post(`/admin/users/${id}/credits`, payload).then((r) => r.data),
  payments: (params) => api.get("/admin/payments", { params }).then((r) => r.data),
  refundPurchase: (id) => api.post(`/admin/purchases/${id}/refund`).then((r) => r.data),
  webhookEvents: (params) => api.get("/admin/webhook-events", { params }).then((r) => r.data),
  retryWebhook: (id) => api.post(`/admin/webhook-events/${id}/retry`).then((r) => r.data),
  aiOperations: (params) => api.get("/admin/ai-operations", { params }).then((r) => r.data),
  noMatchStats: () => api.get("/admin/no-match-stats").then((r) => r.data),
  actionPrices: () => api.get("/admin/action-prices").then((r) => r.data),
  updateActionPrice: (key, payload) => api.patch(`/admin/action-prices/${key}`, payload).then((r) => r.data),
  creditPacks: () => api.get("/admin/credit-packs").then((r) => r.data),
  updateCreditPack: (id, payload) => api.patch(`/admin/credit-packs/${id}`, payload).then((r) => r.data),
  content: () => api.get("/admin/content/packages").then((r) => r.data),
  settings: () => api.get("/admin/settings").then((r) => r.data),
  welcomeBonus: () => api.get("/admin/welcome-bonus").then((r) => r.data),
  updateWelcomeBonus: (p) => api.patch("/admin/welcome-bonus", p).then((r) => r.data),
  updateFlags: (flags) => api.patch("/admin/settings/flags", { flags }).then((r) => r.data),
  creditsSummary: () => api.get("/admin/credits/summary").then((r) => r.data),
  creditsLedger: (params) => api.get("/admin/credits/ledger", { params }).then((r) => r.data),
  creditsWallets: (params) => api.get("/admin/credits/wallets", { params }).then((r) => r.data),
  audit: (params) => api.get("/admin/audit", { params }).then((r) => r.data),
  emailEvents: (params) => api.get("/admin/email-events", { params }).then((r) => r.data),
  testEmail: (payload) => api.post("/admin/settings/test-email", payload).then((r) => r.data),
  faq: () => api.get("/admin/faq").then((r) => r.data),
  faqCategoryCreate: (p) => api.post("/admin/faq/categories", p).then((r) => r.data),
  faqCategoryUpdate: (id, p) => api.patch(`/admin/faq/categories/${id}`, p).then((r) => r.data),
  faqCategoryDelete: (id) => api.delete(`/admin/faq/categories/${id}`).then((r) => r.data),
  faqArticleCreate: (p) => api.post("/admin/faq/articles", p).then((r) => r.data),
  faqArticleUpdate: (id, p) => api.patch(`/admin/faq/articles/${id}`, p).then((r) => r.data),
  faqArticleDelete: (id) => api.delete(`/admin/faq/articles/${id}`).then((r) => r.data),
  deleteMessage: (id) => api.delete(`/admin/messages/${id}`).then((r) => r.data),
  deleteEmailEvent: (id) => api.delete(`/admin/email-events/${id}`).then((r) => r.data),
  financeSettings: () => api.get("/admin/finance/settings").then((r) => r.data),
  updateFinanceSettings: (p) => api.patch("/admin/finance/settings", p).then((r) => r.data),
  fx: () => api.get("/admin/finance/fx").then((r) => r.data),
  fxRefresh: () => api.post("/admin/finance/fx/refresh").then((r) => r.data),
  fxManual: (p) => api.post("/admin/finance/fx/manual", p).then((r) => r.data),
  modelPricing: () => api.get("/admin/finance/model-pricing").then((r) => r.data),
  modelPricingCreate: (p) => api.post("/admin/finance/model-pricing", p).then((r) => r.data),
  fixedCosts: () => api.get("/admin/finance/fixed-costs").then((r) => r.data),
  fixedCostCreate: (p) => api.post("/admin/finance/fixed-costs", p).then((r) => r.data),
  fixedCostUpdate: (id, p) => api.patch(`/admin/finance/fixed-costs/${id}`, p).then((r) => r.data),
  fixedCostDelete: (id) => api.delete(`/admin/finance/fixed-costs/${id}`).then((r) => r.data),
  reconciliations: () => api.get("/admin/finance/reconciliations").then((r) => r.data),
  reconciliationCreate: (p) => api.post("/admin/finance/reconciliations", p).then((r) => r.data),
  aiCostControl: () => api.get("/admin/ai-cost/control").then((r) => r.data),
  aiCostControlUpdate: (p) => api.patch("/admin/ai-cost/control", p).then((r) => r.data),
  aiCostMinOverride: (p) => api.post("/admin/ai-cost/min-credit-override", p).then((r) => r.data),
  aiCostOverrideHistory: () => api.get("/admin/ai-cost/override-history").then((r) => r.data),
  aiCostEconomics: (days = 30) => api.get("/admin/ai-cost/economics", { params: { days } }).then((r) => r.data),
  aiCostBudgetStatus: () => api.get("/admin/ai-cost/budget-status").then((r) => r.data),
  aiCostProfitability: () => api.get("/admin/ai-cost/profitability").then((r) => r.data),
};

export const helpApi = {
  faq: () => api.get("/help/faq").then((r) => r.data),
};

export const cmsApi = {
  catalog: () => api.get("/admin/prompts/catalog").then((r) => r.data),
  reviewQueue: () => api.get("/admin/prompts/review-queue").then((r) => r.data),
  reviewQueueProgress: () => api.get("/admin/prompts/review-queue-progress").then((r) => r.data),
  published: () => api.get("/admin/prompts/published").then((r) => r.data),
  versions: (pkg, slug) => api.get(`/admin/prompts/${pkg}/${slug}/versions`).then((r) => r.data),
  version: (id) => api.get(`/admin/prompts/version/${id}`).then((r) => r.data),
  newPrompt: (p) => api.post("/admin/prompts/new", p).then((r) => r.data),
  draftFromCurrent: (pkg, slug) => api.post(`/admin/prompts/${pkg}/${slug}/draft-from-current`).then((r) => r.data),
  save: (id, p) => api.put(`/admin/prompts/version/${id}`, p).then((r) => r.data),
  autofill: (id) => api.post(`/admin/prompts/version/${id}/autofill-samples`).then((r) => r.data),
  preview: (id) => api.get(`/admin/prompts/version/${id}/preview`).then((r) => r.data),
  qa: (id) => api.post(`/admin/prompts/version/${id}/qa`).then((r) => r.data),
  setStatus: (id, p) => api.post(`/admin/prompts/version/${id}/status`, p).then((r) => r.data),
  publish: (id) => api.post(`/admin/prompts/version/${id}/publish`).then((r) => r.data),
  bulkPublish: (ids) => api.post("/admin/prompts/bulk-publish", { version_ids: ids }).then((r) => r.data),
};
